import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-md sm:hover:bg-primary/90 sm:hover:shadow-lg sm:hover:-translate-y-0.5 active:translate-y-0",
        destructive:
          "bg-destructive text-destructive-foreground shadow-md sm:hover:bg-destructive/90 sm:hover:shadow-lg",
        outline:
          "border-2 border-primary bg-transparent text-primary sm:hover:bg-primary sm:hover:text-primary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm sm:hover:bg-secondary/80",
        ghost: "sm:hover:bg-accent sm:hover:text-accent-foreground",
        link: "text-primary underline-offset-4 sm:hover:underline",
        action:
          "bg-primary text-primary-foreground shadow-md sm:hover:shadow-lg sm:hover:bg-primary/90 sm:hover:-translate-y-0.5 active:translate-y-0 font-semibold tracking-wide [&_svg]:size-5",
      },
      size: {
        default: "h-11 px-5 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-12 rounded-lg px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
